import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { MouseEventHandler, useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import { FormInput } from "../components/custom/components";
import DeleteConfirmSales from "../components/DeleteConfirmSales";
import Modal from "../components/Modal";
import { ToastTypes, useToast } from "../components/toast/ToastProvider";
import { FormType, FormTypeList } from "../config";
import { decodeHtml, formatInrTwoDecimals, getDate, LocalFormatDate, rupeeFormat } from "../config/functions";
import { AuthUserType } from "../redux/actions/authUserActions";
import { ClientRegistrationType } from "../redux/actions/clientRegistration";
import { CustomerType, fetchCustomerAction } from "../redux/actions/customerAction";
import { ProductType } from "../redux/actions/productAction";
import { fetchSalesByInvocieDateAction, SalesType } from "../redux/actions/salesActions";
import { AppDispatch, StoreState } from "../redux/store";
import { getSalesById } from "../services/sales.service ";
import { SalesEditAttributes } from "./SalesMaster";
import { AccountHolderType } from "../redux/actions/accountHolder";
import { getCustomer } from "../services/customer.services";

interface TableBody {
    sno: number;
    invoiceDate: string;
    invoiceNo: string;
    salesEntryNo: string;
    customer: string;
    representer: string;
    finalAmount: string;
    onEdit: MouseEventHandler<HTMLButtonElement>;
    onDelete: MouseEventHandler<HTMLButtonElement>;
    onPrint: MouseEventHandler<HTMLButtonElement>;
}

type HandleEdit = (value: SalesType) => void;
type HandleDelete = (value: SalesType) => void;
type HandlePrint = (value: SalesType) => void;

interface Props {
    setFormType: React.Dispatch<React.SetStateAction<FormType>>;
    setId: React.Dispatch<React.SetStateAction<number | undefined>>;
    handleEdit?: () => void;
    onEditComplete?: () => void;
    onclose: () => void;
    /** When the view modal opens, refetch sales for the selected date */
    viewOpen?: boolean;
}

const toApiInvoiceDate = (date: Date | null): string =>
    getDate(date ?? new Date());

const Sales = (props: Props) => {
    const dispatch = useDispatch<AppDispatch>();
    const toast = useToast();
    const salesList = useSelector<StoreState, SalesType[]>((state) => state.sales);
    const AccountHoldersList = useSelector<StoreState, AccountHolderType[]>((state) => state.account);
    const [customerList, setcustomerList] = useState<CustomerType[]>([]);
    const [showDeleteForm, setShowDeleteForm] = useState<boolean>(false);
    const [searchDate, setSearchDate] = useState<Date | null>(new Date());
    const [searchCustomer, setSearchCustomer] = useState<string>("");
    const [searchInvoiceNo, setSearchInvoiceNo] = useState<string>("");
    const [editData, setEditData] = useState<SalesType>();

    const clients = useSelector<StoreState, ClientRegistrationType[]>((state) => state.clientRegistration);
    const AuthUser = useSelector<StoreState, AuthUserType | null>((state) => state.authUser);
    const ProductList = useSelector<StoreState, ProductType[]>((state) => state.product);

    const fetchCustomer = async () => {
        const res = await getCustomer();
        setcustomerList(res.data.data);
    };

    const loadSalesForDate = (date: Date | null) => {
        dispatch(fetchSalesByInvocieDateAction(toApiInvoiceDate(date)));
    };

    useEffect(() => {
        if (props.viewOpen !== false) {
            loadSalesForDate(searchDate);
            fetchCustomer();
        }
    }, [props.viewOpen, dispatch]);

    const handleDateFilter = (date: Date | null) => {
        setSearchDate(date);
        loadSalesForDate(date);
    };
    const handleCustomerFilter = (value: string) => setSearchCustomer(value.toLowerCase());
    const handleInvoiceNoFilter = (value: string) => setSearchInvoiceNo(value.toLowerCase());

    // ✅ Compare only date parts (ignore time)

    const handleEdit: HandleEdit = (value) => {
        props.setFormType(FormTypeList.EDIT);
        props.setId(value.id);
        setEditData(value);
        props.onclose();
    };

    const handleDelete: HandleDelete = (value) => {
        props.setFormType(FormTypeList.DELETE);
        setEditData(value);
        setShowDeleteForm(true);
    };

    // ✅ FIX 2: Print flow with proper PDF generation
    const handlePrintPdf: HandlePrint = async (value) => {
        if (!value?.id) {
            toast("Invalid sales data", ToastTypes.ERROR);
            return;
        }

        try {
            const response = await getSalesById(value.id);
            const data: SalesEditAttributes = response.data.data;

            const productMap = new Map(
                ProductList?.map((product) => [product.productCode?.toString(), product])
            );

            const printObj = {
                kotnumber: 1,
                invoiceType: data.invoice_type,
                taxable: data.net_taxable,
                cgst: data.net_cgst,
                sgst: data.net_sgst,
                billnumber: data.invoice_number?.toString(),
                tablename: "table1",
                customer: customerList?.find((c) => c.accountNumber?.toString() === data.customer_id?.toString()),
                cashierid: AuthUser?.userId,
                systemid: data.system_no,
                date: LocalFormatDate(data.invoice_date),
                printername: "",
                time: new Date().toLocaleTimeString("en-US", {
                    timeZone: "Asia/Kolkata",
                    hour12: true,
                    hour: "2-digit",
                    minute: "2-digit",
                }),
                // ✅ FIXED TYPO: productitems (was productitmes)
                productitems: data.salesItems
                    ?.filter((item: any) => item.product_code)
                    ?.map((item: any) => {
                        const product = productMap.get(item.product_group_code?.toString());                       
                        return {
                            name: product?.productName || item.product_code,
                            pcode: item.product_code,
                            hsn: product?.sac || "-",
                            qty: item.quantity,
                            rate: parseFloat(item.w_rate || "0").toFixed(2),
                            gst: product?.gst || "0",
                            total: parseFloat(item.total_amount || "0").toFixed(2),
                        };
                    }),
                empname: AuthUser?.name,
                grandtotal: data.grand_total,
                discount: [],
                gst: [],
                oc: [],
                finaltotal: parseFloat(data.grand_total || "0").toFixed(2),
                totaldisc: parseFloat(data.dis_amount || "0").toFixed(2),
                totalqty: data.salesItems?.reduce((sum: number, si: any) => sum + Number(si.total_quantity || 0), 0),
                paymentamount: parseFloat(data.amt_paid || "0").toFixed(2),
                tender: data.tender_amount ? parseFloat(data.tender_amount.toString()).toFixed(2) : "0",
                balance: data.tender_return ? parseFloat(data.tender_return.toString()).toFixed(2) : "0",
            };

            if (!printObj.productitems?.length) {
                toast("No items to print", ToastTypes.ERROR);
                return;
            }

            // Generate and open PDF in new tab for print
            const pdfBlob = generateBillPdf(printObj);
            const pdfUrl = URL.createObjectURL(pdfBlob);
            const printWindow = window.open(pdfUrl, "_blank");

            if (printWindow) {
                printWindow.onload = () => {
                    printWindow.print();
                };
            } else {
                // Fallback: direct download if popup blocked
                const a = document.createElement("a");
                a.href = pdfUrl;
                a.download = `Invoice-${printObj.billnumber}.pdf`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                toast("PDF downloaded. Please allow popups for print preview.");
            }

            setTimeout(() => URL.revokeObjectURL(pdfUrl), 3000);
        } catch (error) {
            console.error("Print error:", error);
            toast("Failed to generate invoice", ToastTypes.ERROR);
        }
    };

    const handleDeleteFormClose = () => {
        setShowDeleteForm(false);
        props.setFormType(FormTypeList.ADD);
        // ✅ Refresh sales list after delete
        loadSalesForDate(searchDate);
    };

    // ✅ FIXED: Returns blob for flexible handling (print/download)
    const generateBillPdf = (printObj: any): Blob => {
        const doc = new jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: "a4",
        });

        let y = 5; // Start higher to save space

        // === Business Header ===
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text(clients[0]?.shopFullName || "", 15, y + 2.5);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.text(
            `${clients[0]?.address1 || ""}, ${clients[0]?.address2 || ""}`,
            15,
            y + 6,
        );
        doc.text(
            `${clients[0]?.district || ""} - ${clients[0]?.pincode || ""}`,
            15,
            y + 9.5,
        );
        doc.text("Cell: " + (clients[0]?.contactNo1 || ""), 15, y + 13);
        doc.text("GST No: " + (clients[0]?.gstNo || ""), 15, y + 16.5);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text("SALES TAX INVOICE", 87, y + 9);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);

        // PhonePe/GPay Image (if element exists)
        const img = document.getElementById("phonepe") as HTMLImageElement | null;
        if (img instanceof HTMLImageElement) {
            doc.addImage(img, "JPEG", 180, y, 15, 15);
        }
        doc.text("Gpay No: " + (clients[0]?.contactNo2 || ""), 166.5, y + 17.5);

        doc.line(15, y + 18.5, 195, y + 18.5); // Top separator line

        // === Customer & Invoice Details ===
        y = y + 15;

        // Customer block (LEFT)
        doc.setFont("helvetica", "normal");
        doc.text(`NAME: ${decodeHtml(printObj.customer?.accountName) || ""}`, 15, y + 6);
        doc.text(`ADDRESS: ${printObj.customer.place} `, 15, y + 9);
        doc.text(`CELL: ${printObj.customer?.contactNumber1 || ""}`, 15, y + 12);
        doc.text(`GSTIN: ${printObj.customer?.gstNo || "NIL"}`, 15, y + 15);

        // Invoice block (RIGHT)
        doc.setFont("helvetica", "bold");
        doc.setFont("helvetica", "normal");
        doc.text(`Invoice No: ${printObj.billnumber}`, 156.5, y + 6);
        doc.text(`Date: ${printObj.date} ${printObj.time}`, 156.5, y + 9);
        doc.text(`Vehicle No: ${printObj.customer.contactNumber2}`, 156.5, y + 12);
        doc.text("Payment: CASH SALES", 156.5, y + 15);

        doc.line(15, y + 16, 195, y + 16); // Separator before table

        // === Product Table ===
        const tableY = y + 16;
        const tableColumn = ["S.No", "Particulars", "HSN", "Qty", "Rate", "GST%", "Total"];
console.log(printObj.productitems);

        const tableRows = printObj?.productitems?.map((item: any, i: number) => [
            i + 1,
            item.name,
            item.hsn || "-",
            item.qty,
            item.rate,
            item.gst,
            item.total,
        ]) || [];

        autoTable(doc, {
            startY: tableY,
            head: [tableColumn],
            body: tableRows,
            theme: "plain",
            styles: {
                fontSize: 8,
                cellPadding: 0.3,
                valign: "middle",
                lineWidth: 0,
            },
            headStyles: {
                textColor: 0,
                fontStyle: "bold",
                halign: "left",
            },
            columnStyles: {
                0: { halign: "center" },
                1: { halign: "left" },
                2: { halign: "left" },
                3: { halign: "center" },
                4: { halign: "right" },
                5: { halign: "center" },
                6: { halign: "right" },
            },
            margin: { left: 15, right: 15 },
            didParseCell: function (data) {
                if (
                    data.section === "head" &&
                    (data.column.index === 4 || data.column.index === 6)
                ) {
                    data.cell.styles.halign = "right";
                }
                if (
                    data.section === "head" &&
                    (data.column.index === 3 || data.column.index === 5)
                ) {
                    data.cell.styles.halign = "center";
                }
            },
            didDrawPage: (data) => {
                if ((doc as any).lastAutoTable) {
                    const table = (doc as any).lastAutoTable;
                    const endY = table.finalY;
                    doc.setDrawColor(0);
                    doc.setLineWidth(0.2);
                    doc.line(15, endY, 195, endY);
                }
            },
            didDrawCell: (data) => {
                if (data.section === "head" && data.row.index === 0) {
                    const { x, y, width, height } = data.cell;
                    data.doc.setDrawColor(0);
                    data.doc.setLineWidth(0.2);
                    data.doc.line(x, y + height, x + width, y + height);
                }
            },
        });

        let finalY = (doc as any).lastAutoTable.finalY;
        doc.line(15, finalY, 195, finalY);

        // === Summary Tables (Side by Side) ===
        const commonY = finalY + 2;
        const leftTableX = 14;   // GST table
        const rightTableX = 110; // Amounts table

        // LEFT: Tax Summary (with Old Balance column)
        autoTable(doc, {
            startY: commonY + 1,
            margin: { left: leftTableX + 5, top: 0, bottom: 0 },
            head: [["Taxable", "CGST", "SGST", "Old Balance"]],
            body: [[
                parseFloat(printObj.taxable || "0").toFixed(2),
                printObj.cgst || "0",
                printObj.sgst || "0",
                (printObj.previousBal || "0") + ".00",
            ]],
            theme: "plain",
            styles: { fontSize: 8, halign: "center", cellPadding: 0.3 },
            tableLineWidth: 0,
            columnStyles: {
                0: { cellWidth: 30 },
                1: { cellWidth: 30 },
                2: { cellWidth: 30 },
                3: { cellWidth: 30 },
            },
        });

        // RIGHT: Payment Summary
        autoTable(doc, {
            startY: commonY - 1, // Align with left table
            margin: { left: rightTableX + 20, top: 0, bottom: 0 },
            body: [
                ["Bill Amount", ":", printObj.finaltotal],
                ["Amount Paid", ":", printObj.finaltotal], // this is just for the current requirement. has to be implemented asap.
                ["Balance", ":", parseFloat(printObj.credit || "0").toFixed(2)],
            ],
            theme: "plain",
            styles: { fontSize: 8, cellPadding: 0.3 },
            tableLineWidth: 0,
            columnStyles: {
                0: { fontStyle: "bold", halign: "right", cellWidth: 35 },
                1: { halign: "right", cellWidth: 5 },
                2: { halign: "right", cellWidth: 25 },
            },
        });

        // Bottom separator line
        const lineY = Math.max(
            (doc as any).lastAutoTable.finalY,
            finalY
        );
        doc.line(15, lineY, 195, lineY);

        // Footer
        doc.text(
            "Goods Once Sold Cannot be taken back. Thanks Come Again.",
            67,
            finalY + 20,
        );

        // ✅ Return Blob for flexible handling (print/download)
        return doc.output("blob");
    };

    // ✅ FIX 1: Proper date filtering with isSameDate helper
    const filteredSales = salesList?.filter((tr) => {
        const invoiceNoMatch = searchInvoiceNo
            ? tr.invoiceNumber?.toLowerCase().includes(searchInvoiceNo)
            : true;

        const customerObj = customerList.find(
            (s) => s?.accountNumber?.toString() === tr?.customerId?.toString()
        );
        const customerName = customerObj?.accountName?.toLowerCase() || "";
        const customerMatch = searchCustomer
            ? customerName.includes(searchCustomer)
            : true;

        return invoiceNoMatch && customerMatch;
    });

    return (
        <>
            <div className="row mb-3 align-items-center">
                <div className="col-lg-8">
                    <div className="hstack gap-4">
                        <div>
                            <label htmlFor="searchDate">Date :</label>
                            <DatePicker
                                id="searchDate"
                                selected={searchDate}
                                onChange={handleDateFilter}
                                dateFormat="dd-MM-yyyy"
                                className="form-control form-control-sm"
                            />
                        </div>
                        <FormInput
                            name="searchInvoiceNo"
                            label="Invoice No :"
                            value={searchInvoiceNo}
                            onChange={(e) => handleInvoiceNoFilter(e.target.value)}
                            containerClass="mb-0"
                        />
                        <FormInput
                            name="searchCustomer"
                            label="Customer :"
                            value={searchCustomer}
                            onChange={(e) => handleCustomerFilter(e.target.value)}
                            containerClass="mb-0"
                        />
                    </div>
                </div>
                <div className="col-lg-4 col-md-6 col-sm-6">
                    <p className="text-center fs-3 fw-bold m-0">{"Total Amount : "} <span className="text-primary">{"₹"+formatInrTwoDecimals(filteredSales.reduce((acc, i) => acc + Number(i.grandTotal), 0))}</span></p>
                </div>
            </div>

            <Modal headerText={"Delete Sales"} visible={showDeleteForm} onClose={handleDeleteFormClose} centered size="sm">
                <DeleteConfirmSales
                    editData={editData}
                    onClose={() => {
                        handleDeleteFormClose();
                        // ✅ FIX 1: Refresh after delete
                        loadSalesForDate(searchDate);
                    }}
                />
            </Modal>

            <div className="row">
                <div className="col-lg-12">
                    {/* <div className="row">
                        <div className="col-lg-5 col-md-0 col-sm-0"></div>
                        <div className="col-lg-5 col-md-6 col-sm-6"></div>
                        <div className="col-lg-2 col-md-6 col-sm-6 display-flex gap-0">
                            <p className="text-center fs-3 fw-bold">Total Amount</p>
                            <p className="text-center fs-3 fw-bold">
                                {"₹"+filteredSales.reduce((acc, i) => acc + Number(i.grandTotal), 0)}
                            </p>
                        </div>
                    </div> */}
                    <div className="card shadow-lg">
                        <div className="table-wrapper">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th className="text-truncate align-middle">S.No</th>
                                        <th className="text-truncate align-middle">Sales Entry No</th>
                                        <th className="text-truncate align-middle">Bill Date</th>
                                        <th className="text-truncate align-middle">Bill No</th>
                                        <th className="text-truncate align-middle">Customer</th>
                                        <th className="text-truncate align-middle">Representer</th>
                                        <th className="text-truncate align-middle">Grand Total</th>
                                        <th className="text-truncate align-middle">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredSales?.map((tr, i) => {
                                        const customerObj = customerList?.find((s) => s?.accountNumber?.toString() === tr?.customerId?.toString());
                                        const representerObj = AccountHoldersList?.find(
                                            (s) => {
                                                 return s?.accountNumber?.toString() === tr?.repId?.toString()}
                                        );

                                        const customerName = decodeHtml(customerObj?.accountName || "");
                                        const repName = decodeHtml(representerObj?.accountName || "N/A");

                                        return (
                                            <UserBody
                                                sno={i + 1}
                                                invoiceNo={tr.invoiceNumber?.toString()}
                                                invoiceDate={tr.invoiceDate?.toString()}
                                                salesEntryNo={tr.salesEntryNumber?.toString()}
                                                finalAmount={tr.grandTotal?.toString() || "0"}
                                                customer={customerName}
                                                representer={repName}
                                                onEdit={() => handleEdit(tr)}
                                                onDelete={() => handleDelete(tr)}
                                                onPrint={() => handlePrintPdf(tr)}
                                                key={tr.id || i}
                                            />
                                        );
                                    })}
                                    {filteredSales?.length === 0 && (
                                        <tr>
                                            <td colSpan={8} className="text-center py-4">No records found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

const UserBody = ({ sno, invoiceDate, finalAmount, invoiceNo, salesEntryNo, customer, representer, onEdit, onDelete, onPrint }: TableBody) => {
    const AuthUser = useSelector<StoreState, AuthUserType | null>((state) => state.authUser);
    return (
        <tr key={sno}>
            <td className="align-middle">{sno}</td>
            <td className="text-capitalize align-middle">{salesEntryNo}</td>
            <td className="text-capitalize align-middle">{LocalFormatDate(invoiceDate)}</td>
            <td className="text-capitalize align-middle">{invoiceNo}</td>
            <td className="text-capitalize align-middle">{customer}</td>
            <td className="text-capitalize align-middle">{representer}</td>
            <td className="text-capitalize align-middle">{rupeeFormat(finalAmount)}</td>
            <td className="align-middle">
                <div className="hstack gap-1">
                    {AuthUser?.role === "SUPER" && (
                        <button className="btn btn-sm fw-bold btn-info p-1" onClick={onEdit}>
                            <i className="fe-edit noti-icon"></i>
                        </button>
                    )}
                    {AuthUser?.role === "SUPER" && (
                        <button className="btn btn-sm fw-bold btn-danger p-1" onClick={onDelete}>
                            <i className="fe-trash-2 noti-icon"></i>
                        </button>
                    )}
                    <button className="btn btn-sm fw-bold btn-success p-1" onClick={onPrint}>
                        <i className="fe-printer noti-icon"></i>
                    </button>
                </div>
            </td>
        </tr>
    );
};

export default Sales;